import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { parseQuery } from '@common/typescript/utils/url';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { getLocalStorageValue, setLocalStorageValue } from '@common/react/utils/localStorage/localStorage';
import useRequest from '@common/react/hooks/useRequest';
import Pagination from '@common/react/components/base/Pagination/Pagination';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { capitalizeFirstLetter } from '@common/react/utils/utils';

import CaseModalInner from '@commonTuna/react/components/UI/CaseModalInner/CaseModalInner';
import { Case } from '@commonTuna/react/objects/BaseCase';

import BeforeAfterBreast from '@app/components/UI/BeforeAfter/BeforeAfterBreast';
import BeforeAfterFilters, {
	Filter, initialFilterState,
} from '@app/components/Pages/BeforeAfterList/BeforeAfterFilters';
import LockContent from '@app/components/UI/LockContent/LockContent';
import { selectProps } from '@app/components/UI/BseSelect';

import '@app/scss/pages/beforeAfter.scss';
import '@app/scss/components/beforeAfter.scss';
import '@app/scss/components/caseModal.scss';

interface ModalState {
	visible: boolean;
	caseId: number | null;
	index: number | null;
}

const initialModalState: ModalState = {
	visible: false,
	caseId: null,
	index: null,
};

interface BeforeAfterMainProps {
	initialValues: any;
}

enum Mode {
	Full,
	Short
}

export const BeforeAfterMain = (props: BeforeAfterMainProps) => {
	const request = useRequest();
	const {
		state: {
			items, pagination, loading, filters,
		}, actions: { handleChange },
	} = useItemsProviderContext<Case>();
	const [modalState, setModalState] = React.useState(initialModalState);
	const [firstOpen, setFirstOpen] = React.useState(false);
	const [showFilter, setVisibilityFilter] = React.useState(false);
	const [mode, _setMode] = React.useState<Mode>(Mode.Short);
	const ref = React.useRef(-1);
	const { t, i18n } = useTranslation();
	const lang = capitalizeFirstLetter(i18n.language);
	const { renderModal } = useModal();

	React.useEffect(() => {
		_setMode(+getLocalStorageValue('before-after-mode', Mode.Short));
	}, []);

	const setMode = (mode) => {
		setLocalStorageValue('before-after-mode', mode);
		_setMode(mode);
	};

	const closeDialog = () => {
		setModalState((prevState) => ({
			...prevState,
			visible: false,
			index: null,
		}));
	};

	const handleCaseClick = (caseId: number) => {
		setVisibilityFilter(false);
		setModalState((prevState) => ({
			...prevState,
			visible: true,
			caseId,
		}));
		setFirstOpen(true);
	};

	const findCurrCaseIndex = () => {
		if (!items) return;
		const currCase = items.filter((c) => c.id === modalState.caseId)[0];
		const index = items.indexOf(currCase);
		return index >= 0 ? index : undefined;
	};

	const onArrowClick = (number = 1) => {
		const currIndex = findCurrCaseIndex();
		if (!items) return;

		setModalState((prev) => {
			const newCurrentIndex = (currIndex || 0) + (pagination.current - 1) * pagination.pageSize + number;
			const newIndex = currIndex !== undefined && currIndex > -1
				? newCurrentIndex < 0 ? pagination.total - 1
					: newCurrentIndex >= pagination.total ? 0 : newCurrentIndex
				: prev.index && prev.index + number === pagination.total ? 0
					: prev.index && prev.index + number === -1 ? pagination.total - 1
						: (prev.index || 0) + number;
			ref.current = newIndex;
			if (currIndex === undefined || currIndex === 0 || currIndex + 1 === items.length) {
				request('caseViewListRemote', {
					...filters,
					pageSize: 1,
					page: newIndex,
					offset: newIndex,
				}).then((res: any) => {
					if (ref.current === newIndex) {
						setModalState((prev) => ({
							...prev,
							caseId: res.list[0].id,
						}));
					}
				});
			}

			return ({
				...prev,
				caseId: currIndex !== undefined && currIndex > 0 && currIndex + 1 < items.length ? items[currIndex + number].id : -1,
				index: newIndex,
			});
		});
	};

	const handleLeftClick = () => {
		onArrowClick(-1);
	};

	const handleRightClick = () => {
		onArrowClick();
	};

	return <>
		<div className="clearfix">
			<button
				type="button"
				className="general-form-button before-after__toggle-filters ml10"
				onClick={() => setVisibilityFilter(true)}
			>
				<i className="fa fa-sliders" />
			</button>
			<div className="pull-right ml10 btn-group">
				<button
					type="button"
					className={`btn btn-default ${mode === Mode.Short ? 'btn-selected' : ''}`}
					onClick={() => setMode(Mode.Short)}
				>
					{t('site.short')}
				</button>
				<button
					type="button"
					className={`btn btn-default ${mode === Mode.Full ? 'btn-selected' : ''}`}
					onClick={() => setMode(Mode.Full)}
				>
					{t('site.full')}
				</button>
			</div>
		</div>
		<div className="clearfix is-relative before-after__content">
			<LockContent fixed />
			<div
				onClick={() => setVisibilityFilter(false)}
				className={`before-after__filters-wrapper__mask ${showFilter ? 'mask_show' : ''}`}
			/>
			<div className={`before-after__filters-wrapper ${showFilter ? 'before-after__filters-wrapper_show' : ''}`}>
				<i className="fa fa-times before-after__filters-wrapper-close" onClick={() => setVisibilityFilter(false)} />
				<BeforeAfterFilters
					initialValues={props.initialValues}
				/>
			</div>
			<div className="before-after__cases-wrapper">
				{items.length > 0 ? items.map((item: Case, index: number) => <BeforeAfterBreast
					key={`${item.id}`}
					isShort={mode === Mode.Short}
					item={item}
					onCaseClick={handleCaseClick}
				/>) : <h3 className="text-center">Items Not Found</h3>}
				{loading && items.length > 0
					? <div key="loading" className="before-after__cases-wrapper_loading"><Loader /></div> : null}
				{pagination.total > 10
					? (
						<Pagination
							key="pagination"
							className="dark-pagination"
							current={pagination.current}
							pageSize={pagination.pageSize}
							total={pagination.total ? pagination.total : 0}
							onChange={(page) => handleChange({ page })}
							selectProps={selectProps}
						/>
					) : null
				}
			</div>

		</div>
		{renderModal(modalState.visible, closeDialog, {
			afterClose: closeDialog,
			className: 'dark-modal case-modal',
			style: { width: '100%' },
			children: <CaseModalInner
				lang={lang}
				t={t}
				caseId={modalState.caseId}
				leftClick={handleLeftClick}
				rightClick={handleRightClick}
			/>,
		})}
	</>;
};

const BeforeAfterList: React.FC<{initLoad?: boolean, serverPage? }> = ({ initLoad, serverPage }) => {
	const { t } = useTranslation();
	const location = useLocation();

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;

	return <div className="before-after container inner-page">
		<BreadCrumbs title={t('seo.beforeAndAfterPage.title')} />
		<ItemsProvider<Case, Filter>
			ttl={10000}
			items={serverPage?.items?.list}
			filters={{ ...initialFilterState }}
			pagination={{ current: defCurrent, pageSize: 10, total: serverPage?.items?.count }}
			loadRequest="caseViewListRemote"
			withHandleUrl
			pageInSearch
			skipInitLoad={!initLoad}
			transformFiltersBeforeHandleUrl={(filters) => ({
				...defaultTransformFiltersBeforeHandleUrl(filters),
				page: filters.page || 1,
			})}
		>
			<BeforeAfterMain
				initialValues={serverPage?.filterProps}
			/>
		</ItemsProvider>
	</div>;
};

export default BeforeAfterList;
